import { FC, ReactElement, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";

import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  useCreateIeepaRefundCapeReportMutation,
  useGetAllReportsQuery,
} from "@/services";
import { tokens } from "@/tokens";

import IeepaRefundCapeDetail from "./detail";

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 180000;

const IeepaRefundCape: FC = (): ReactElement => {
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => s.client);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);

  const reportQueryVars = useMemo(() => {
    const base: any = { pageNo: 1, pageSize: 50, onlyExports: true };
    if (
      user?.userRole &&
      [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole)
    ) {
      base.clientId = selectedClient?._id;
    }
    if (user?.userRole && [roles.TDADMIN].includes(user.userRole)) {
      base.tenantId = selectedTenant?._id;
    }
    return base;
  }, [user, selectedClient?._id, selectedTenant?._id]);

  const {
    data: reportsData,
    isFetching,
    refetch,
  } = useGetAllReportsQuery(reportQueryVars, {
    skip: !user,
    refetchOnMountOrArgChange: true,
  });

  const latestReportId = useMemo(() => {
    const all = (reportsData?.data || []) as IReport[];
    const ieepa = all.filter(
      (r) => r.reportType === "IEEPA_REFUND_CAPE" && !!r.s3Key
    );
    if (ieepa.length === 0) return null;
    const sorted = [...ieepa].sort((a, b) => {
      const at = new Date(a.createdAt ?? 0).getTime();
      const bt = new Date(b.createdAt ?? 0).getTime();
      return bt - at;
    });
    return sorted[0]._id;
  }, [reportsData]);

  const isTDAdminTCD =
    user?.userRole === roles.TDADMIN &&
    selectedTenant?.tenantType === "TRADEDIVE_CLIENT";

  const canGenerate = useMemo(() => {
    if (!user?.userRole) return false;
    const allowed = [
      roles.SPOWNER,
      roles.SPPARTNER,
      roles.SPCLIENT,
      roles.TDADMIN,
      roles.TDCLIENT,
    ];
    if (!allowed.includes(user.userRole)) return false;
    // SP_CLIENT and TD_CLIENT are auto-scoped via their token (clientId
    // derived server-side from custom:userId / custom:tenantId) — they
    // never use the client picker. Only SP_OWNER/SP_PARTNER/TD_ADMIN need
    // an explicit selectedClient before generation can run.
    const needsClient = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(
      user.userRole
    );
    if (needsClient && !selectedClient?._id && !isTDAdminTCD) return false;
    return true;
  }, [user, selectedClient, isTDAdminTCD]);

  const scopeKey = `${selectedTenant?._id ?? ""}:${selectedClient?._id ?? ""}`;

  const [createReport] = useCreateIeepaRefundCapeReportMutation();
  const [pendingReportId, setPendingReportId] = useState<string | null>(null);
  const [hasTriggered, setHasTriggered] = useState(false);
  // Sticky failure flag — set when createReport throws, returns no id, or
  // polling hits the timeout. Drives the "Couldn't build" + Try again UI
  // and is cleared by retry. Distinguishes a real failure from the brief
  // in-flight gap between effect fire and the createReport response landing.
  const [creationFailed, setCreationFailed] = useState(false);
  const pollRef = useRef<number | null>(null);
  const generationScopeRef = useRef<string | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Reset auto-generation state when scope (client/tenant) changes.
  useEffect(() => {
    setPendingReportId(null);
    setHasTriggered(false);
    setCreationFailed(false);
    generationScopeRef.current = null;
    stopPolling();
  }, [scopeKey]);

  useEffect(() => () => stopPolling(), []);

  // Auto-trigger generation when this scope has no completed report.
  useEffect(() => {
    if (!user || !canGenerate) return;
    if (isFetching) return;
    if (!reportsData) return;
    if (latestReportId) return;
    if (hasTriggered) return;

    setHasTriggered(true);
    const myScope = scopeKey;
    generationScopeRef.current = myScope;

    const input: { clientId?: string; tenantId?: string } = {};
    if (
      user.userRole &&
      [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole)
    ) {
      input.clientId = selectedClient?._id;
    }
    if (user.userRole && [roles.TDADMIN].includes(user.userRole)) {
      input.tenantId = selectedTenant?._id;
    }

    (async () => {
      try {
        const res = await createReport(input).unwrap();
        const newId = res?.data;
        if (generationScopeRef.current !== myScope) return;
        if (!newId) {
          setCreationFailed(true);
          return;
        }
        setPendingReportId(newId);
        const start = Date.now();
        pollRef.current = window.setInterval(async () => {
          if (generationScopeRef.current !== myScope) {
            stopPolling();
            return;
          }
          const result = await refetch();
          const latest = (result.data?.data || []) as IReport[];
          const target = latest.find((r) => r._id === newId);
          const done = !!target?.s3Key;
          const timedOut = Date.now() - start > POLL_TIMEOUT_MS;
          if (done || timedOut) {
            stopPolling();
            setPendingReportId(null);
            if (timedOut && !done) setCreationFailed(true);
          }
        }, POLL_INTERVAL_MS);
      } catch {
        // Surface the failure rather than leaving the user on a stale
        // spinner. handleRetry resets creationFailed + hasTriggered to
        // re-arm the effect.
        if (generationScopeRef.current === myScope) setCreationFailed(true);
      }
    })();
  }, [
    user,
    canGenerate,
    isFetching,
    reportsData,
    latestReportId,
    hasTriggered,
    scopeKey,
    selectedClient?._id,
    selectedTenant?._id,
    createReport,
    refetch,
  ]);

  const isInitialLoading = isFetching && !reportsData;
  // Polling is live (createReport returned an id; the interval is running).
  const isGenerating = !latestReportId && !!pendingReportId;
  // Sticky failure — only true after createReport actually failed (threw,
  // returned no id) or polling timed out without seeing s3Key. NOT true
  // during the brief in-flight window between effect fire and createReport
  // landing — that's covered by isPreparing below.
  const isStuck = !latestReportId && creationFailed;
  // Pre-trigger (effect hasn't fired yet) or in-flight (effect fired,
  // createReport hasn't resolved). Shows the building spinner so users
  // never see the "Couldn't build" error while we're still working.
  const isPreparing =
    !latestReportId && canGenerate && !isGenerating && !isStuck;

  const handleRetry = (): void => {
    setHasTriggered(false);
    setPendingReportId(null);
    setCreationFailed(false);
    generationScopeRef.current = null;
    stopPolling();
  };

  if (isInitialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!latestReportId && !canGenerate) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ height: "100%", minHeight: 0, flex: 1, p: 4 }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: `${tokens.brand.primary}08`,
            border: `2px dashed ${tokens.brand.primary}30`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FileDownload sx={{ fontSize: 32, color: `${tokens.brand.primary}80` }} />
        </Box>
        <Box sx={{ maxWidth: 380, textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: tokens.fontSize.md,
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
              mb: 0.5,
            }}
          >
            Select a client to view refunds
          </Typography>
          <Typography
            sx={{
              fontSize: tokens.fontSize.sm2,
              color: tokens.text.muted,
              lineHeight: 1.5,
            }}
          >
            Choose a client from the selector to load IEEPA refund analytics.
          </Typography>
        </Box>
      </Stack>
    );
  }

  if (isGenerating || isPreparing) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ height: "100%", minHeight: 0, flex: 1, p: 4 }}
      >
        <CircularProgress size={36} sx={{ color: tokens.brand.primary }} />
        <Box sx={{ maxWidth: 420, textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: tokens.fontSize.md,
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
              mb: 0.5,
            }}
          >
            Building IEEPA refund analytics
          </Typography>
          <Typography
            sx={{
              fontSize: tokens.fontSize.sm2,
              color: tokens.text.muted,
              lineHeight: 1.5,
            }}
          >
            Crunching the latest ACE data for this client. This usually takes a
            minute or two.
          </Typography>
        </Box>
      </Stack>
    );
  }

  if (isStuck) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ height: "100%", minHeight: 0, flex: 1, p: 4 }}
      >
        <Box sx={{ maxWidth: 420, textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: tokens.fontSize.md,
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
              mb: 0.5,
            }}
          >
            Couldn’t build the report
          </Typography>
          <Typography
            sx={{
              fontSize: tokens.fontSize.sm2,
              color: tokens.text.muted,
              lineHeight: 1.5,
              mb: 2,
            }}
          >
            The IEEPA refund analytics didn’t finish. This can happen if the
            request failed or timed out.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={handleRetry}
            sx={{ bgcolor: tokens.brand.primary }}
          >
            Try again
          </Button>
        </Box>
      </Stack>
    );
  }

  // Should be unreachable — every other state is handled above. Returning
  // null narrows latestReportId from `string | null` to `string` for the
  // detail render below and keeps the component safe if a future branch
  // change introduces a gap.
  if (!latestReportId) return <></>;

  const detailKey = `${scopeKey}:${latestReportId}`;

  return (
    <IeepaRefundCapeDetail
      key={detailKey}
      reportId={latestReportId}
      hideBackButton
    />
  );
};

export default IeepaRefundCape;
